/**
 * OfflineAudioContext でフレーズをレンダリングし、16bit PCM WAV に
 * エンコードする。シンセグラフは synthVoice.ts を共用するため、
 * リアルタイム再生と同じ音がそのまま書き出される。
 */

import { flattenArrangement, type Arrangement } from "./arrangement";
import { ticksToSeconds } from "./phraseGenerator";
import {
  NOTE_RELEASE_SEC,
  createSynthChain,
  estimateDelayTail,
  scheduleVoice,
  wobbleSeedFor,
  type SynthParams,
} from "./synthVoice";

/** AudioBuffer → 16bit PCM WAV（RIFF）バイト列 */
export const encodeWav = (buffer: AudioBuffer): ArrayBuffer => {
  const numChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const numFrames = buffer.length;
  const bytesPerSample = 2;
  const blockAlign = numChannels * bytesPerSample;
  const dataSize = numFrames * blockAlign;

  const arrayBuffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(arrayBuffer);

  const writeString = (offset: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(offset + i, s.charCodeAt(i));
  };

  writeString(0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeString(8, "WAVE");
  writeString(12, "fmt ");
  view.setUint32(16, 16, true); // fmt チャンクサイズ
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true); // ビット深度
  writeString(36, "data");
  view.setUint32(40, dataSize, true);

  const channels: Float32Array[] = [];
  for (let ch = 0; ch < numChannels; ch++) channels.push(buffer.getChannelData(ch));

  let offset = 44;
  for (let frame = 0; frame < numFrames; frame++) {
    for (let ch = 0; ch < numChannels; ch++) {
      const sample = Math.max(-1, Math.min(1, channels[ch][frame]));
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
      offset += 2;
    }
  }

  return arrayBuffer;
};

/**
 * アレンジメント（メイン + SOUND A/B）をオフラインレンダリングして
 * WAV バイト列を返す。長さ = 全レイヤー末尾 + リリース + ディレイテール。
 */
export const renderArrangementToWav = async (
  arrangement: Arrangement,
  bpm: number,
  synthParams: SynthParams,
): Promise<ArrayBuffer> => {
  const sampleRate = 48000;
  const events = flattenArrangement(arrangement);

  const oscFor = {
    main: synthParams.osc,
    soundA: synthParams.soundAOsc,
    soundB: synthParams.soundBOsc,
  } as const;

  const endSec = events.reduce((max, { note }) => {
    const startSec = ticksToSeconds(note.tick, bpm);
    const end = startSec + ticksToSeconds(note.durationTicks, bpm);
    return Math.max(max, end);
  }, 0);
  const tailSec = estimateDelayTail(synthParams, bpm) + NOTE_RELEASE_SEC + 0.2;
  const totalSec = endSec + tailSec;

  const ctx = new OfflineAudioContext(
    2,
    Math.ceil(totalSec * sampleRate),
    sampleRate,
  );
  const chain = createSynthChain(ctx, ctx.destination, synthParams, bpm);

  for (const { layer, note } of events) {
    const startTime = ticksToSeconds(note.tick, bpm);
    scheduleVoice(ctx, chain.input, {
      osc: oscFor[layer],
      midiNote: note.midiNote,
      velocity: note.velocity,
      startTime,
      durationSec: ticksToSeconds(note.durationTicks, bpm),
      glideToMidi: note.glideToMidi,
      glideSec: synthParams.glideMs / 1000,
      gold: synthParams.goldOn,
      goldRateHz: synthParams.goldRateHz,
      kaitlyn: synthParams.kaitlynOn,
      wobbleSeed: wobbleSeedFor(layer, note),
    });
  }

  const rendered = await ctx.startRendering();
  return encodeWav(rendered);
};
